import { FC, MutableRefObject, useCallback, useState } from 'react';

import { Menu, Popover, Transition } from '@headlessui/react';
import { FaPlus } from 'react-icons/fa6';
import { Button } from 'ui/components/common';
import { cn } from 'utils/helpers';
import _ from 'lodash';
import { MdClose } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { useCan as useCanRolePermissions } from 'utils/providers/RolePermission';
import { ACTIONS, PERMISSION_KEYS } from 'utils/constants';
import { useGetSubscription } from 'modules/account/settings/hooks';

import Lead from './modes/Lead';
import Company from './modes/Company';
import Deal from './modes/Deal';
import Task from './modes/Task';
import Event from './modes/Event';
import { TOUR_SELECTOR } from '../Tour';

const GENERAL_CREATE_OPTION_SELECTORS = {
  GENERAL_CREATE_LEAD: 'GENERAL_CREATE_LEAD',
  GENERAL_CREATE_COMPANY: 'GENERAL_CREATE_COMPANY',
  GENERAL_CREATE_DEAL: 'GENERAL_CREATE_DEAL',
  GENERAL_CREATE_INVOICE: 'GENERAL_CREATE_INVOICE',
  GENERAL_CREATE_TASK: 'GENERAL_CREATE_TASK',
  GENERAL_CREATE_EVENT: 'GENERAL_CREATE_EVENT',
  GENERAL_CREATE_BOOKING: 'GENERAL_CREATE_BOOKING',
};

const GlobalCreate = () => {
  const navigate = useNavigate();
  const { data: subscription } = useGetSubscription();

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [mode, setMode] = useState<
    'lead' | 'company' | 'deal' | 'invoice' | 'task' | 'event' | 'booking'
  >('lead');

  const canLead = useCanRolePermissions(ACTIONS.CREATE, PERMISSION_KEYS.LEADS);
  const canCompany = useCanRolePermissions(
    ACTIONS.CREATE,
    PERMISSION_KEYS.COMPANIES,
  );
  const canDeal = useCanRolePermissions(ACTIONS.CREATE, PERMISSION_KEYS.DEALS);
  const canInvoice = useCanRolePermissions(
    ACTIONS.CREATE,
    PERMISSION_KEYS.ORDERS,
  );
  const canTask = useCanRolePermissions(ACTIONS.CREATE, PERMISSION_KEYS.TASKS);
  const canEvent = useCanRolePermissions(
    ACTIONS.CREATE,
    PERMISSION_KEYS.EVENTS,
  );
  const canBooking = useCanRolePermissions(
    ACTIONS.CREATE,
    PERMISSION_KEYS.BOOKINGS,
  );

  const hasAccessToCreateButton =
    canLead ||
    canCompany ||
    canDeal ||
    canInvoice ||
    canTask ||
    canEvent ||
    canBooking;

  const items = [
    {
      selector: GENERAL_CREATE_OPTION_SELECTORS.GENERAL_CREATE_LEAD,
      label: 'Lead',
      onClick: () => setMode('lead'),
      hidden: !canLead,
    },
    {
      selector: GENERAL_CREATE_OPTION_SELECTORS.GENERAL_CREATE_COMPANY,
      label: 'Company',
      onClick: () => setMode('company'),
      hidden: !canCompany,
    },
    {
      selector: GENERAL_CREATE_OPTION_SELECTORS.GENERAL_CREATE_DEAL,
      label: 'Deal',
      onClick: () => setMode('deal'),
      hidden: !canDeal,
    },
    {
      selector: GENERAL_CREATE_OPTION_SELECTORS.GENERAL_CREATE_INVOICE,
      label: 'Invoice',
      onClick: () => navigate('sales/invoice/new/'),
      hidden: !canInvoice,
    },
    {
      selector: GENERAL_CREATE_OPTION_SELECTORS.GENERAL_CREATE_TASK,
      label: 'Task',
      onClick: () => setMode('task'),
      hidden: !canTask,
    },
    {
      selector: GENERAL_CREATE_OPTION_SELECTORS.GENERAL_CREATE_EVENT,
      label: 'Event',
      onClick: () => setMode('event'),
      hidden: !canEvent,
    },
    {
      selector: GENERAL_CREATE_OPTION_SELECTORS.GENERAL_CREATE_BOOKING,
      label: 'Booking',
      onClick: () => navigate('calendar/bookings/new'),
      hidden: !canBooking,
    },
  ];

  return (
    <Popover>
      <Menu as="div" className="relative inline-block">
        {!!hasAccessToCreateButton && (
          <Menu.Button
            as={Button}
            data-tut={TOUR_SELECTOR.TOUR.GENERAL_CREATE.CREATE}
            left
            icon={() => <FaPlus className="w-3 h-3" />}
            label="CREATE"
            size="sm"
            disabled={subscription?.isExpired}
          />
        )}
        <Transition
          className={cn(
            'shadow-leapify-xl',
            'absolute top-full left-0',
            'bg-fill-tertiary',
            'min-w-[200px]',
            'font-bold text-sm text-secondary-dark',
            'mt-2 p-0 z-50',
            'rounded-md overflow-hidden',
          )}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items className={'focus:outline-none focus:ring-0'}>
            {items.map((item) => (
              <Menu.Item key={item.label}>
                {({ active, close }) => (
                  <Popover.Button
                    as="div"
                    data-tut={`TOUR_${item.selector}`}
                    role="button"
                    onClick={(e) => {
                      e.preventDefault();
                      item.onClick();
                      setIsPopoverOpen(true);
                      close();
                    }}
                    className={cn(
                      item.hidden && 'hidden',
                      'py-3 px-4 transition',
                      'cursor-pointer',
                      'bg-fill-tertiary',
                      'font-normal',
                      active ? 'text-primary-base' : 'text-content',
                      'hover:text-primary-base hover:font-semibold',
                    )}
                  >
                    Create {item.label}
                  </Popover.Button>
                )}
              </Menu.Item>
            ))}
          </Menu.Items>
        </Transition>
      </Menu>
      <GlobalCreatePopover
        setIsOpen={setIsPopoverOpen}
        isOpen={isPopoverOpen}
        mode={mode}
      />
    </Popover>
  );
};

export default GlobalCreate;

type GlobalCreatePopoverProps = {
  mode: 'lead' | 'company' | 'deal' | 'invoice' | 'task' | 'event' | 'booking';
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

export type ComponentToRenderGenericProps = {
  close: (
    focusableElement?: HTMLElement | MutableRefObject<HTMLElement | null>,
  ) => void;
};

const GlobalCreatePopover: FC<GlobalCreatePopoverProps> = ({
  mode,
  isOpen,
  setIsOpen,
}) => {
  const componentToRender = useCallback(
    (close: ComponentToRenderGenericProps['close']) => {
      switch (mode) {
        case 'lead':
          return <Lead close={close} />;
        case 'company':
          return <Company close={close} />;
        case 'deal':
          return <Deal close={close} />;
        case 'task':
          return <Task close={close} />;
        case 'event':
          return <Event close={close} />;
        case 'invoice':
          return <div>invoice</div>;
        case 'booking':
          return <div>booking</div>;
        default:
          return <Lead close={close} />;
      }
    },
    [mode],
  );

  return (
    <Transition
      show={isOpen}
      enter="transform transition ease-in-out duration-300"
      enterFrom="translate-x-full"
      enterTo="translate-x-90"
      leave="transform transition ease-in-out duration-300"
      leaveFrom="translate-x-90"
      leaveTo="translate-x-full"
      className={cn('fixed bottom-0 right-0 z-50')}
    >
      <Popover.Panel static={isOpen}>
        {() => (
          <div
            className={cn(
              'w-[500px]',

              'rounded-[6px]',
              'overflow-hidden',
              'shadow-leapify-xl',
              'overflow-y-auto',
            )}
          >
            <div
              data-tut="global_create_panel_header"
              className={cn(
                'bg-fill-primary',
                'border-b-[0.5px] border-separator-primary',
                'flex justify-between',
                'py-3 px-[18px]',
              )}
            >
              <div className="flex items-center gap-2 text-secondary-dark font-semibold text-sm">
                Create {_.startCase(mode)}
              </div>
              <MdClose
                title="Close"
                type="button"
                role="button"
                onClick={() => setIsOpen?.(false)}
              />
            </div>
            <div className="space-y-7 p-[22px] bg-fill-tertiary pb-8">
              {componentToRender(() => setIsOpen?.(false))}
            </div>
          </div>
        )}
      </Popover.Panel>
    </Transition>
  );
};
